// src/features/clinical/components/prescription/PrescriptionView.jsx
import React from 'react';
import { FaPrint, FaDownload, FaPrescription, FaUser, FaCalendar, FaUserMd } from 'react-icons/fa';
import { formatDate, formatFrequency } from '../../utils/clinicalHelpers';

const PrescriptionView = ({ prescription, onPrint, onDownload }) => {
  if (!prescription) {
    return (
      <div className="text-center py-8 text-gray-500">
        No prescription selected
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FaPrescription className="mr-2 text-green-600" />
            Prescription
          </h1>
          <p className="text-sm text-gray-500 mt-1">#{prescription.prescriptionId}</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={onPrint}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <FaPrint className="mr-2" /> Print
          </button>
          <button
            onClick={onDownload}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
          >
            <FaDownload className="mr-2" /> Download
          </button>
        </div>
      </div>

      {/* Hospital Info */}
      <div className="text-center mb-8 pb-4 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">City General Hospital</h2>
        <p className="text-sm text-gray-600">123 Healthcare Ave, Medical District</p>
        <p className="text-sm text-gray-600">Tel: (555) 123-4567 | License: HOSP-001</p>
      </div>

      {/* Patient & Doctor Info */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
            <FaUser className="mr-2" /> Patient Information
          </h3>
          <p className="font-medium text-gray-900">{prescription.patientName}</p>
          <p className="text-sm text-gray-600">ID: {prescription.patientId}</p>
          <p className="text-sm text-gray-600">Age: {prescription.patientAge} years</p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
            <FaUserMd className="mr-2" /> Prescribed By
          </h3>
          <p className="font-medium text-gray-900">{prescription.doctorName}</p>
          <p className="text-sm text-gray-600">License: {prescription.doctorLicense}</p>
          <p className="text-sm text-gray-600">
            <FaCalendar className="inline mr-1" /> {formatDate(prescription.prescriptionDate)}
          </p>
        </div>
      </div>

      {/* Medicines */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Prescribed Medicines</h3>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medicine</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dosage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instructions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {prescription.medicines.map((med, index) => (
              <tr key={index}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{med.name}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{med.dosage}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{formatFrequency(med.frequency)}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{med.duration}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{med.quantity}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{med.instructions || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tests */}
      {prescription.tests && prescription.tests.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recommended Tests</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            {prescription.tests.map((test, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                <div>
                  <p className="font-medium text-gray-900">{test.name}</p>
                  <p className="text-sm text-gray-500">{test.instructions}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  test.priority === 'URGENT' ? 'bg-red-100 text-red-600' :
                  test.priority === 'STAT' ? 'bg-purple-100 text-purple-600' :
                  'bg-green-100 text-green-600'
                }`}>
                  {test.priority}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Doctor's Notes */}
      {prescription.notes && (
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Doctor's Notes</h3>
          <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{prescription.notes}</p>
        </div>
      )}

      {/* Follow-up */}
      {prescription.followUpDate && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Follow-up Date:</strong> {formatDate(prescription.followUpDate)}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-gray-200 text-center text-sm text-gray-500">
        <p>This prescription is valid only with doctor's signature</p>
        <p className="mt-1">Generated on {formatDate(new Date())}</p>
      </div>
    </div>
  );
};

export default PrescriptionView;